import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Boxes, Wallet2, LayoutDashboard, Lock, Cpu, Globe, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/AppLayout";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PublicMobileNav } from "@/components/PublicMobileNav";
import { Loader } from "@/components/Loader";

export default function Landing() {
  const [booting, setBooting] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 1100);
    return () => clearTimeout(t);
  }, []);

  if (booting) return <Loader label="Initializing Portal" />;

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] sm:w-[900px] lg:w-[1200px] max-w-[120vw] h-[400px] sm:h-[600px] bg-[#00F0FF]/10 blur-[100px] sm:blur-[140px] rounded-full pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-4 sm:px-6 md:px-12 py-4 sm:py-5">
        <div className="flex items-center gap-3">
          <PublicMobileNav />
          <Logo />
        </div>
        <nav className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:block"><ThemeToggle /></div>
          <Link to="/login" data-testid="nav-login" className="hidden sm:inline-block px-3 sm:px-4 py-2 text-sm text-white/85 hover:text-white">Sign in</Link>
          <Link
            to="/register"
            data-testid="nav-register"
            className="px-3 sm:px-4 py-2 text-sm rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] hover:shadow-[0_0_22px_rgba(0,240,255,0.45)] transition-all whitespace-nowrap"
          >
            Get Started
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-12 sm:pb-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF] text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] font-bold mb-5 sm:mb-6 animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] pulse-glow" /> Web3 Incubation Platform
          </div>
          <h1 className="font-display font-black text-[34px] sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] animate-fade-up" style={{ animationDelay: "0.1s" }}>
            The portal for <br />
            <span className="bg-gradient-to-r from-[#00F0FF] via-[#7DD3FC] to-[#007AFF] bg-clip-text text-transparent neon-text">
              Web3 project incubation
            </span>
          </h1>
          <p className="text-[#94A3B8] mt-6 sm:mt-7 text-base sm:text-lg max-w-xl lg:max-w-lg mx-auto lg:mx-0 leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
            SpectralX is a blockchain incubation, contribution management, and client portal platform for Web3 projects — bringing deposits, contributions, and program participation into one secure dashboard.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mt-8 sm:mt-9 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Link
              to="/register"
              data-testid="hero-cta-register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] hover:shadow-[0_0_28px_rgba(0,240,255,0.55)] transition-all"
            >
              Open an Account <ArrowRight size={18} />
            </Link>
            <Link
              to="/login"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#121E3D] border border-white/10 text-white hover:border-[#00F0FF]/40 transition-all"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* 3D animated scene */}
        <div className="relative h-[300px] sm:h-[380px] flex items-center justify-center animate-fade-up" style={{ animationDelay: "0.25s" }}>
          <div className="absolute w-64 h-64 sm:w-80 sm:h-80 rounded-full border border-[#00F0FF]/20 animate-spin-slow" />
          <div className="absolute w-48 h-48 sm:w-60 sm:h-60 rounded-full border border-[#007AFF]/20 animate-spin-slow" style={{ animationDirection: "reverse", animationDuration: "18s" }} />
          {/* orbiting dots */}
          <div className="absolute w-64 h-64 sm:w-80 sm:h-80 animate-spin-slow" style={{ animationDuration: "14s" }}>
            <span className="orbit-dot" style={{ top: 0, left: "50%" }} />
            <span className="orbit-dot" style={{ bottom: 0, left: "50%", background: "#007AFF", boxShadow: "0 0 14px #007AFF" }} />
          </div>
          {/* floating cube */}
          <div className="tilt-card animate-floaty">
            <div
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#007AFF] flex items-center justify-center shadow-[0_0_60px_rgba(0,240,255,0.45)]"
              style={{ transform: "rotateX(18deg) rotateY(-22deg)" }}
            >
              <span className="font-display font-black text-[#060B19] text-5xl sm:text-6xl">S</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <div className="glass-strong rounded-2xl grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
          {[
            { v: "BEP20", l: "Network" },
            { v: "24/7", l: "Portal Access" },
            { v: "Vetted", l: "Admin Review" },
            { v: "Secure", l: "Encrypted Auth" },
          ].map((s) => (
            <div key={s.l} className="p-5 sm:p-6 text-center">
              <div className="font-display text-2xl sm:text-3xl font-black text-[#00F0FF]">{s.v}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#94A3B8] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        {[
          { Icon: Boxes, t: "Project Incubation", d: "A structured home for Web3 ventures — from onboarding to active participation programs." },
          { Icon: Wallet2, t: "Contribution Management", d: "Deposit USDT (BEP20), allocate to participation programs, and track every plan in one place." },
          { Icon: LayoutDashboard, t: "Unified Client Portal", d: "A real-time dashboard for balances, contributions, withdrawals, and account activity." },
          { Icon: ShieldCheck, t: "Admin Vetted", d: "Every deposit and withdrawal is reviewed and approved by the SpectralX operations team." },
          { Icon: Cpu, t: "On-chain Ready", d: "Built for the BNB Smart Chain ecosystem with transaction-tracked transfers." },
          { Icon: Globe, t: "Global Access", d: "Manage your account anywhere with a fast, responsive, mobile-first experience." },
        ].map(({ Icon, t, d }, i) => (
          <div key={t} className="glass p-6 hover:border-[#00F0FF]/30 transition-all animate-fade-up" style={{ animationDelay: `${0.05 * i}s` }}>
            <Icon className="text-[#00F0FF]" size={26} strokeWidth={1.5} />
            <div className="font-display text-xl font-bold mt-4">{t}</div>
            <div className="text-[#94A3B8] text-sm mt-2 leading-relaxed">{d}</div>
          </div>
        ))}
      </section>

      {/* How it works */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <h2 className="font-display text-2xl sm:text-3xl font-black text-center mb-8">How it works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          {[
            { n: "01", t: "Create your account", d: "Register in seconds and get a unique SpectralX User ID." },
            { n: "02", t: "Deposit & contribute", d: "Fund your wallet via USDT (BEP20) and start a participation program." },
            { n: "03", t: "Track & withdraw", d: "Monitor activity in real time and withdraw to your BEP20 wallet." },
          ].map((s) => (
            <div key={s.n} className="glass p-6 relative overflow-hidden">
              <div className="absolute -top-3 -right-2 font-display font-black text-6xl text-white/5">{s.n}</div>
              <CheckCircle2 className="text-[#00F0FF]" size={22} />
              <div className="font-display text-lg font-bold mt-3">{s.t}</div>
              <div className="text-[#94A3B8] text-sm mt-2 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-6 text-center text-xs text-[#475569] tracking-wider">
        <Lock size={12} className="inline mr-2" />
        SpectralX © 2026 — Blockchain incubation, contribution management & client portal for Web3 projects.
      </footer>
    </div>
  );
}
